import { useCallback, useEffect, useState } from 'react';

/**
 * useActiveProject
 * ────────────────
 * Persists the user's "currently working on" project across pages so that
 * jumping from Submit → Approvals → Allotment doesn't reset the picker
 * back to the first project (the previous behaviour, which led to PMs
 * accidentally working on the wrong episode).
 *
 * Storage: localStorage so the choice survives reloads. Keyed per logged-in
 * user (when their id is provided) so two PMs sharing a workstation don't
 * step on each other.
 */
const STORAGE_KEY = 'vos_active_project_id';

const _key = (userId) => userId ? `${STORAGE_KEY}::${userId}` : STORAGE_KEY;

export function getActiveProject(userId) {
  try { return localStorage.getItem(_key(userId)) || ''; } catch { return ''; }
}

export function setActiveProject(projectId, userId) {
  try {
    if (projectId) localStorage.setItem(_key(userId), projectId);
    else localStorage.removeItem(_key(userId));
  } catch { /* ignore quota/SSR */ }
}

export default function useActiveProject(userId) {
  const [projectId, setProjectIdState] = useState(() => getActiveProject(userId));

  const setProjectId = useCallback((id) => {
    setActiveProject(id, userId);
    setProjectIdState(id || '');
  }, [userId]);

  // Sync across browser tabs (e.g. PM has Submit open in tab A, Approvals in tab B)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === _key(userId)) setProjectIdState(e.newValue || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);

  return [projectId, setProjectId];
}
